import React, { forwardRef, useState, useRef, useEffect, createContext, useContext } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '../utils/cn'

export interface DropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  trigger: React.ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'bottom' | 'left' | 'right'
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

interface DropdownContextValue {
  close: () => void
}

const DropdownContext = createContext<DropdownContextValue | null>(null)

const useDropdownContext = () => {
  const context = useContext(DropdownContext)
  if (!context) {
    throw new Error('useDropdownContext must be used within a Dropdown')
  }
  return context
}

const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  ({ 
    children, 
    className, 
    trigger, 
    align = 'start', 
    side = 'bottom',
    open: controlledOpen,
    onOpenChange,
    ...props 
  }, ref) => {
    const [internalOpen, setInternalOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    
    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen
    
    const handleToggle = () => {
      const newOpen = !isOpen
      if (controlledOpen === undefined) {
        setInternalOpen(newOpen)
      }
      onOpenChange?.(newOpen)
    }

    const handleClose = () => {
      if (controlledOpen === undefined) {
        setInternalOpen(false)
      }
      onOpenChange?.(false)
    }

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          handleClose()
        }
      }

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          handleClose()
        }
      }

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscape)
      }
    }, [isOpen])

    const alignmentClasses = {
      start: 'left-0',
      center: 'left-1/2 -translate-x-1/2',
      end: 'right-0',
    }

    const sideClasses = {
      top: 'bottom-full mb-2',
      bottom: 'top-full mt-2',
      left: 'right-full mr-2',
      right: 'left-full ml-2',
    }

    return (
      <DropdownContext.Provider value={{ close: handleClose }}>
        <div
          ref={dropdownRef}
          className={cn('relative inline-block', className)}
          {...props}
        >
          <div onClick={handleToggle}>
            {trigger}
          </div>
          
          {isOpen && (
            <div
              className={cn(
                'absolute z-[9999] min-w-[8rem] max-h-[50vh] overflow-y-auto overflow-x-hidden rounded-md border border-slate-200 bg-white p-1 text-slate-950 shadow-md',
                alignmentClasses[align],
                sideClasses[side]
              )}
            >
              {children}
            </div>
          )}
        </div>
      </DropdownContext.Provider>
    )
  }
)

const DropdownItem = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, onClick, ...props }, ref) => {
    const { close } = useDropdownContext()
    
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(event)
      close()
    }

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-slate-100 focus:bg-slate-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {children}
      </div>
    )
  }
)

const DropdownSeparator = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-slate-100', className)}
      {...props}
    />
  )
)

const DropdownLabel = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('px-2 py-1.5 text-sm font-semibold text-slate-900', className)}
      {...props}
    />
  )
)

Dropdown.displayName = 'Dropdown'
DropdownItem.displayName = 'DropdownItem'
DropdownSeparator.displayName = 'DropdownSeparator'
DropdownLabel.displayName = 'DropdownLabel'

export { Dropdown, DropdownItem, DropdownSeparator, DropdownLabel } 